import React, { Component, useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import api from '@/shared/api';
import config from '@/config';
import { CanCreate, CanDelete, CanUpdate, showWhenPermission } from '@/shared/components/security';
import authService, { privileges } from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import handleHttpError from '@/shared/services/HandleHttpError';
import enumCache from '@/shared/services/EnumCacheService';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import SimpleFileUpload from '@/shared/components/input/SimpleFileUpload';
import TextSearch from '@/shared/components/input/TextSearch';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import EditableSelect from '@/shared/components/editable/EditableSelect';
import EditableTextbox from '@/shared/components/editable/EditableTextbox';
import DataCacheService from '@/shared/services/DataCacheService';
import YesNoModal from '@/shared/components/YesNoModal';
import ToastrService from '@/shared/services/ToastrService';
import SignalRService from '@/shared/services/SignalRService';

import {
  AddIcon,
  DeleteIcon,
  DownloadIcon,
  EyeIcon,
  IconPencil,
  ReplaceIcon,
  SpinnerIcon,
  CopyIcon,
  SaveIcon,
  UndoIcon,
  OneDriveIcon,
} from '@/shared/components/Icons';
import SimplePanel from '@/shared/components/SimplePanel';
import CollapsablePanel from '@/shared/components/CollapablePanel';
import DocumentService from '@/shared/services/DocumentService';
import PreviewDocumentTemplateModal from './PreviewDocumentTemplateModal';
import messages from './documentTemplateMessages';

import AddDocumentTemplateModal from './AddDocumentTemplateModal';
import PlaceholdersModal from './PlaceholdersModal';
import TaskPlaceholdersModal from './TaskPlaceholdersModal';
import DocumentTemplateExamples from './DocumentTemplateExamples';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';

const SessionStorageKey = 'DocumentTemplatesSettings';
const linkedRelationPlaceholderUrls = {
  nl:
    'https://static.adminpulse.eu/public/Documentation%20files/nl/AdminPulse_Invulvelden_gelinkte_relaties.docx',
  fr:
    'https://static.adminpulse.eu/public/Documentation%20files/fr/AdminPulse_Substituants_relations_liées.docx',
  en:
    'https://static.adminpulse.eu/public/Documentation%20files/en/AdminPulse_Placeholders_linked_relations.docx',
};
const invoiceTemplateTypes = [0, 1, 2, 11, 12, 14, 15, 16, 17];

class DocumentTemplateContainer extends Component {
  state = {
    templates: null,
    templateTypes: null,
    companies: [],
    search: '',
    filters: {
      templateType: null,
      language: null,
      relationType: null,
      companyId: null,
    },
    uploading: false,
    showAddModal: false,
    showPlaceholdersModal: false,
    showTaskPlaceholdersModal: false,
    deleteTemplate: null,
    examplesCollapsed: false,
    tenantConfiguration: 0,
    loadingDocumentDetails: false,
    showCheckinDocumentModal: false,
    checkinTemplate: null,
    templateCheckoutInfo: null,
    actionsInProgress: false,
  };

  constructor(props) {
    super(props);
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    this.state = {
      ...this.state,
      ...sessionParams,
    };
  }

  documentCheckedoutHandler = data => {
    const { intl } = this.props;

    // Handle error object with ErrorCode
    if (data && typeof data === 'object' && data.ErrorCode) {
      const type = data.Type || 'connectexceptioncode';
      ToastrService.error(intl.formatMessage({ id: `${type}_${data.ErrorCode}` }));
      this.setState({ actionsInProgress: false });
      return;
    }

    const documentEditLink = typeof data === 'object' ? data.EditLink : data;
    ToastrService.success(intl.formatMessage(messages.CheckedOutSuccesfull));
    this.setState({ actionsInProgress: false });
    DocumentService.tryOpenUrl(documentEditLink);
  };

  documentCheckedinHandler = blobId => {
    this.setState({ actionsInProgress: false });
  };

  componentDidMount() {
    const { intl } = this.props;

    SignalRService.subscribe('checkoutFinished', this.documentCheckedoutHandler);
    SignalRService.subscribe('checkinFinished', this.documentCheckedinHandler);

    Promise.all([enumCache.get('templatetype'), enumCache.get('relationtype')]).then(
      ([templateTypes, relationTypes]) => {
        const dataCache = DataCacheService.getDataCache();
        this.setState({
          templateTypes: templateTypes
            .filter(x => invoiceTemplateTypes.indexOf(x.key) === -1) // Exclude invoice types
            .map(x => ({
              id: x.key,
              label: intl.formatMessage(listItemMsg('templatetype', x.key, x.value)),
            }))
            .sort(dynamicSort('label')),
          languages: dataCache.language
            .map(x => ({
              id: x.code,
              label: intl.formatMessage(listItemMsg('language', x.code, x.description || '')),
              isDefault: x.isDefault,
            }))
            .sort(dynamicSort('label')),
          relationTypes: relationTypes
            .map(x => ({
              id: `${x.key}`,
              label: intl.formatMessage(listItemMsg('relationtype', x.key, x.value)),
            }))
            .sort(dynamicSort('label')),
          companies: dataCache.company
            .map(x => ({ ...x, label: x.name }))
            .sort(dynamicSort('label')),
          tenantConfiguration: dataCache.tenantConfiguration || 0,
        });
      },
    );
    this.loadTemplates();
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('checkoutFinished', this.documentCheckedoutHandler);
    SignalRService.unsubscribe('checkinFinished', this.documentCheckedinHandler);
  }

  loadTemplateCheckoutInfo = () => {
    const { templates } = this.state;
    if (!templates || !Array.isArray(templates)) {
      return;
    }
    const templateIds = templates.map(t => t.documentId);
    DocumentService.getTemplateCheckoutInfo(templateIds).then(data => {
      this.setState({ templateCheckoutInfo: data });
    });
  };

  removeCheckOutStatus = template => {
    const { templateCheckoutInfo } = this.state;
    this.setState({
      templateCheckoutInfo: {
        ...templateCheckoutInfo,
        [template.documentId]: undefined,
      },
    });
  };

  loadTemplates = () => {
    const { filters } = this.state;
    api.base.get(`${config.appUrls.api}/api/DocumentTemplate/Templates`, { params: filters }).then(
      ({ data }) => {
        this.setState({
          templates: data,
        });
        this.loadTemplateCheckoutInfo();
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  filterChanged = filter => {
    const { filters } = this.state;
    const newFilters = { ...filters, ...filter };
    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      this.setState({ filters: newFilters }, () => {
        this.setSessionParams();
        this.loadTemplates();
      });
    }
  };
  updateTemplate = (template, changes) => {
    const model = { ...template, ...changes };
    const { intl } = this.props;
    if (JSON.stringify(model) === JSON.stringify(template)) return;
    api.base.put(`${config.appUrls.api}/api/documentTemplate/UpdateTemplate/`, model).then(
      ({ data }) => {
        if (!data) return;
        this.updateTemplateRow(data);
        ToastrService.changesSaved(intl);
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  updateTemplateRow = template => {
    this.setState(({ templates }) => ({
      templates: templates.map(t => {
        if (t.id !== template.id) return t;
        return template;
      }),
    }));
  };

  downloadTemplate = template => DocumentService.download(template.documentId);
  previewTemplate = template => {
    this.setState({ activeTemplate: template, showPreviewModal: true });
  };

  replaceTemplate = (template, [file]) => {
    if (!file) return;

    this.setState({ uploading: template.id });
    const formData = new FormData();
    formData.append('data', file);

    api.base
      .post(`${config.appUrls.api}/api/documentTemplate/ReplaceTemplate/${template.id}`, formData, {
        headers: {
          'Content-Type': undefined,
        },
      })
      .then(
        ({ data }) => {
          if (!data) return;

          this.updateTemplateRow(data);
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      )
      .finally(() => {
        this.setState({ uploading: false });
      });
  };
  deleteTemplate = template => {
    api.base.delete(`${config.appUrls.api}/api/documentTemplate/Delete/${template.id}`).then(
      () => {
        this.setState(({ templates }) => ({
          templates: templates.filter(t => t.id !== template.id),
          deleteTemplate: null,
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  copyTemplate = template => {
    api.base.post(`${config.appUrls.api}/api/documentTemplate/Copy/${template.id}`).then(
      async response => {
        const copiedTemplate = response.data; // Assuming response.data contains the copied template

        const { intl } = this.props;

        // Update the state to include the new template
        const { templates } = this.state;
        const sourceTemplateIndex = templates.findIndex(t => t.id === template.id);

        if (sourceTemplateIndex !== -1) {
          // Create a new array to hold the updated templates list
          const updatedTemplates = [
            ...templates.slice(0, sourceTemplateIndex + 1), // Copy elements before the source template (including the source template)
            copiedTemplate, // Add the copied template after the source template
            ...templates.slice(sourceTemplateIndex + 1), // Copy elements after the source template
          ];
          // Update state with the new templates array
          this.setState({
            templates: updatedTemplates,
          });
        }

        ToastrService.changesSaved(intl);
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  appendTemplate = template => {
    this.setState(({ templates }) => ({
      templates: !template ? templates : [...templates, template],
      showAddModal: false,
    }));
  };
  setSessionParams = (changes = {}) => {
    const { examplesCollapsed, filters } = this.state;
    const data = {
      examplesCollapsed,
      filters,
      ...changes,
    };

    sessionStorage.setItem(SessionStorageKey, JSON.stringify(data));
  };

  toggleExamples = collapsed => {
    this.setState({ examplesCollapsed: collapsed }, () => this.setSessionParams());
  };

  getFilteredTemplates = () => {
    const { templates, search } = this.state;

    if (!search) return templates;

    const parts = search.split(' ');
    let result = templates;

    parts.forEach(term => {
      result = result.filter(x => x.name && x.name.toLowerCase().indexOf(term.toLowerCase()) >= 0);
    });

    return result;
  };

  onCheckinDocument = template => {
    this.checkinDocument(template);
  };

  onDiscardCheckout = template => {
    this.setState({
      showCheckinDocumentModal: true,
      checkinTemplate: template,
      actionsInProgress: true,
    });
  };

  discardCheckout = template => {
    const { intl } = this.props;
    DocumentService.discardCheckout(template.documentId).then(
      () => {
        this.setState({
          checkinTemplate: null,
          showCheckinDocumentModal: false,
          actionsInProgress: false,
        });
        this.loadTemplateCheckoutInfo();
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        this.setState({
          showCheckinDocumentModal: false,
          actionsInProgress: false,
          actionsInProgress: false,
        });
        log.error('error checking in document ', error);
      },
    );
  };

  checkinDocument(template) {
    const { intl } = this.props;
    this.setState({ actionsInProgress: true });
    DocumentService.checkin(template.documentId).then(
      () => {
        this.setState(
          {
            showCheckinDocumentModal: false,
            actionsInProgress: false,
          },
          () => this.removeCheckOutStatus(template),
        );
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        this.setState({
          showCheckinDocumentModal: false,
          actionsInProgress: false,
        });
        log.error('error checking in document ', error);
      },
    );
  }

  checkoutDocument = (template, openinApp) => {
    const { intl } = this.props;
    this.setState({ actionsInProgress: true });
    DocumentService.checkout(template.documentId, openinApp).then(
      data => {
        if (!!data) {
          this.setState({
            actionsInProgress: false,
          });
          this.loadTemplateCheckoutInfo();
        }
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.error(intl.formatMessage(messages.MsgCheckoutDocumentSuccess));
        }
        this.setState({ actionsInProgress: false });
        log.error('error checking out document ', error);
      },
    );
  };

  isCheckedOut = documentId => {
    const { templateCheckoutInfo } = this.state;
    return !!templateCheckoutInfo ? templateCheckoutInfo[documentId] : false;
  };

  isCheckedOutByCurrentUser = documentId => {
    const { templateCheckoutInfo } = this.state;
    return !!templateCheckoutInfo
      ? templateCheckoutInfo[documentId] === authService.getUserId()
      : false;
  };

  setCheckoutInfo = documentId => {
    this.setState({
      isDocumentCheckedOut: this.isCheckedOut(documentId),
      isDocumentCheckedOutByCurrentUser: this.isCheckedOutByCurrentUser(documentId),
    });
  };

  render() {
    const {
      templateTypes,
      filters,
      languages,
      relationTypes,
      companies,
      uploading,
      showAddModal,
      showPlaceholdersModal,
      showTaskPlaceholdersModal,
      deleteTemplate,
      examplesCollapsed,
      showPreviewModal,
      activeTemplate,
      tenantConfiguration,
      search,
      showCheckinDocumentModal,
      checkinTemplate,
      templateCheckoutInfo,
      actionsInProgress,
      isDocumentCheckedOut,
      isDocumentCheckedOutByCurrentUser,
    } = this.state;
    const { intl } = this.props;
    const readOnly = !authService.hasPermission({
      functionality: 'settings',
      privilege: privileges.Update,
    });

    const defaultLanguage = ((languages || []).find(x => x.isDefault) || { id: 'nl' }).id;
    const multipleCompanies = companies.length > 1;

    const languageCode = intl.locale.split('-')[0];
    const { [languageCode]: linkedRelationPlaceholderUrl = null } = linkedRelationPlaceholderUrls;

    const filteredTemplates = this.getFilteredTemplates();

    const checkedOutUserId = showCheckinDocumentModal
      ? templateCheckoutInfo[checkinTemplate.documentId]
      : null;

    const checkedOutUserName = checkedOutUserId
      ? DataCacheService.getUserName(checkedOutUserId)
      : null;
    return (
      <div id="DocumentTemplateOverviewPage">
        <div className="panel">
          <div className="panel-heading">
            <h3 className="panel-title">
              <span>{intl.formatMessage(messages.DocumentTemplates)}</span>
              <ZendeskHelp article="5434751200018" />
            </h3>
            <div className="panel-buttons">
              <button
                className="btn btn-default"
                onClick={() => this.setState({ showPlaceholdersModal: true })}
                title={intl.formatMessage(messages.ShowAvailablePlaceholders)}
                type="button"
              >
                <FormattedMessage {...messages.Placeholders} />
              </button>
              {linkedRelationPlaceholderUrl && (
                <a
                  className="btn btn-default"
                  href={linkedRelationPlaceholderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={intl.formatMessage(messages.ShowAvailablePlaceholders)}
                >
                  <FormattedMessage {...messages.RelationLinksPlaceholders} />
                </a>
              )}
              <button
                className="btn btn-default"
                onClick={() => this.setState({ showTaskPlaceholdersModal: true })}
                title={intl.formatMessage(messages.ShowAvailablePlaceholders)}
                type="button"
              >
                <FormattedMessage {...messages.TaskPlaceholders} />
              </button>
              <CanCreate on="settings">
                <button
                  type="button"
                  className="btn btn-inverse"
                  disabled={!!uploading}
                  onClick={() => this.setState({ showAddModal: true })}
                >
                  {uploading === true ? <SpinnerIcon /> : <i className="fa fa-plus" />}
                  <FormattedMessage {...messages.Add} />
                </button>
              </CanCreate>
            </div>
          </div>
          <div className="panel-body container-fluid">
            <div className="row">
              <div className="col-md-3">
                <TextSearch
                  className="form-control"
                  value={search}
                  placeholder={intl.formatMessage(messages.Search)}
                  onChange={value => this.setState({ search: value || '' })}
                  delay={200}
                />
              </div>
              <div className="col-md-3">
                <select
                  id="TemplateTypeFilter"
                  name="TemplateTypeFilter"
                  className="form-control"
                  onChange={e =>
                    this.filterChanged({
                      templateType: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  value={filters.templateType || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllTemplateTypes)}</option>
                  {templateTypes &&
                    templateTypes.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-3">
                <select
                  id="LanguageFilter"
                  name="LanguageFilter"
                  className="form-control"
                  onChange={e =>
                    this.filterChanged({
                      language: e.target.value || null,
                    })
                  }
                  value={filters.language || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllLanguages)}</option>
                  {languages &&
                    languages.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                </select>
              </div>
              {multipleCompanies && (
                <div className="col-md-3">
                  <select
                    id="CompanyFilter"
                    name="CompanyFilter"
                    className="form-control"
                    onChange={e =>
                      this.filterChanged({
                        companyId: e.target.value || null,
                      })
                    }
                    value={filters.companyId || ''}
                  >
                    <option value="">{intl.formatMessage(messages.AllCompanies)}</option>
                    {companies &&
                      companies.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
            <CollapsablePanel
              title={intl.formatMessage(messages.ExampleDocumentTemplates)}
              collapsed={examplesCollapsed}
              collapseChanged={this.toggleExamples}
              className="control-panel subsection"
            >
              <DocumentTemplateExamples
                intl={intl}
                languageCode={filters.language}
                templateType={filters.templateType}
                search={search}
                tenantConfiguration={tenantConfiguration}
                tenantLanguages={languages?.map(l => l.id) ?? []}
              />
            </CollapsablePanel>
          </div>
        </div>
        {filteredTemplates && (
          <table className="table table-grid table-hover margin5T">
            <thead>
              <tr>
                <th>
                  <FormattedMessage {...messages.Name} />
                </th>
                <th>
                  <FormattedMessage {...messages.DocumentTemplateType} />
                </th>
                <th>
                  <FormattedMessage {...messages.Language} />
                </th>
                <th>
                  <FormattedMessage {...messages.RelationType} />
                </th>
                {multipleCompanies && (
                  <th>
                    <FormattedMessage {...messages.OwnCompany} />
                  </th>
                )}
                <th className="actions-column">
                  <FormattedMessage {...messages.Actions} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map(template => (
                <tr key={template.id}>
                  <td>
                    <EditableTextbox
                      value={template.name}
                      required
                      onChange={name => this.updateTemplate(template, { name })}
                      readOnly={readOnly}
                    />
                  </td>
                  <td>
                    {templateTypes && (
                      <EditableSelect
                        value={template.templateType}
                        onChange={templateType => this.updateTemplate(template, { templateType })}
                        options={templateTypes}
                        required
                        readOnly={readOnly}
                        bindToId
                      />
                    )}
                  </td>
                  <td>
                    {languages && (
                      <EditableSelect
                        value={template.languageCode}
                        onChange={languageCode => this.updateTemplate(template, { languageCode })}
                        options={languages}
                        required
                        readOnly={readOnly}
                        bindToId
                      />
                    )}
                  </td>
                  <td>
                    {relationTypes && (
                      <EditableSelect
                        value={`${template.relationType}`}
                        onChange={relationType => this.updateTemplate(template, { relationType })}
                        placeholder={intl.formatMessage(messages.All)}
                        displayValue={
                          template.relationType === null ? (
                            <FormattedMessage {...messages.All} />
                          ) : (
                            <FormattedListValue list="relationtype" id={template.relationType} />
                          )
                        }
                        readOnly={readOnly}
                        options={relationTypes}
                        bindToId
                      />
                    )}
                  </td>
                  {multipleCompanies && (
                    <td>
                      {companies && (
                        <EditableSelect
                          value={`${template.companyId}`}
                          onChange={companyId => this.updateTemplate(template, { companyId })}
                          placeholder={intl.formatMessage(messages.All)}
                          displayValue={
                            template.companyId === null ? (
                              <FormattedMessage {...messages.All} />
                            ) : (
                              companies.find(x => x.id === template.companyId).name
                            )
                          }
                          readOnly={readOnly}
                          options={companies}
                          bindToId
                        />
                      )}
                    </td>
                  )}
                  <td className="text-right">
                    <div className="flex-row-nowrap">
                      {this.isCheckedOut(template.documentId) && (
                        <div className="btn-group margin10R">
                          <span
                            className="btn btn-xs btn-inverse"
                            title={intl.formatMessage(messages.DocumentCheckedOut, {
                              name: DataCacheService.getUserName(
                                templateCheckoutInfo[template.documentId],
                              ),
                            })}
                          >
                            <OneDriveIcon />
                          </span>
                        </div>
                      )}
                      <a
                        title={intl.formatMessage(messages.Preview)}
                        onClick={() => this.previewTemplate(template)}
                        className="btn btn-xs btn-icon"
                      >
                        <EyeIcon />
                      </a>
                      {!DataCacheService.hasUserConnectPlatform(46) && (
                        <CanUpdate on="settings">
                          <a
                            onClick={() =>
                              DocumentService.edit(template.documentId, intl.locale.split('-')[0])
                            }
                            title={intl.formatMessage(messages.Edit)}
                            className="btn btn-xs btn-icon"
                          >
                            <IconPencil />
                          </a>
                        </CanUpdate>
                      )}
                      <a
                        title={intl.formatMessage(messages.Download)}
                        onClick={() => this.downloadTemplate(template)}
                        className="btn btn-xs btn-icon"
                      >
                        <DownloadIcon />
                      </a>
                      <CanUpdate on="settings">
                        <SimpleFileUpload
                          onUpload={files => this.replaceTemplate(template, files)}
                          accept="application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          disabled={!!uploading}
                        >
                          {uploading && uploading === template.id ? (
                            <SpinnerIcon />
                          ) : (
                            <ReplaceIcon />
                          )}
                        </SimpleFileUpload>
                      </CanUpdate>
                      <CanUpdate on="settings">
                        <a
                          className="btn btn-xs btn-icon"
                          title={intl.formatMessage(messages.Copy)}
                          onClick={() => this.copyTemplate(template)}
                        >
                          <CopyIcon />
                        </a>
                      </CanUpdate>
                      <CanDelete on="settings">
                        <a
                          className="btn btn-xs btn-icon"
                          onClick={() => this.setState({ deleteTemplate: template })}
                        >
                          <DeleteIcon />
                        </a>
                      </CanDelete>
                      {(DataCacheService.hasUserConnectPlatform(46) ||
                        (this.isCheckedOut(template.documentId) &&
                          authService.hasPermission({
                            functionality: 'CompanyOwn',
                            privilege: 'Update',
                          }))) && (
                        <CanUpdate on="settings">
                          <Dropdown
                            id={`splitbutton-${template.documentId}`}
                            className="btn-group-sm btn-group-dropdown"
                            pullRight
                            style={{ float: 'right' }}
                            onClick={() => this.setCheckoutInfo(template.documentId)}
                          >
                            <Dropdown.Toggle bsStyle="default" bsSize="xsmall">
                              <IconPencil />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {!isDocumentCheckedOut &&
                                DataCacheService.hasUserConnectPlatform(46) && (
                                  <MenuItem
                                    key={intl.formatMessage(messages.CheckoutApp)}
                                    onSelect={() => this.checkoutDocument(template, true)}
                                    disabled={actionsInProgress}
                                  >
                                    <IconPencil className="margin5R" />
                                    {intl.formatMessage(messages.CheckoutApp)}
                                  </MenuItem>
                                )}
                              {!isDocumentCheckedOut &&
                                DataCacheService.hasUserConnectPlatform(46) && (
                                  <MenuItem
                                    key={intl.formatMessage(messages.CheckoutBrowser)}
                                    onSelect={() => this.checkoutDocument(template, false)}
                                    disabled={actionsInProgress}
                                  >
                                    <IconPencil className="margin5R" />
                                    {intl.formatMessage(messages.CheckoutBrowser)}
                                  </MenuItem>
                                )}
                              {isDocumentCheckedOutByCurrentUser &&
                                DataCacheService.hasUserConnectPlatform(46) && (
                                  <MenuItem
                                    key={intl.formatMessage(messages.ContinueEditing)}
                                    onSelect={() =>
                                      DocumentService.getCheckedOutLink(template.documentId).then(
                                        link => {
                                          if (!link) return;
                                          window.open(link);
                                        },
                                      )
                                    }
                                    disabled={actionsInProgress}
                                  >
                                    <IconPencil className="margin5R" />
                                    {intl.formatMessage(messages.ContinueEditing)}
                                  </MenuItem>
                                )}
                              {isDocumentCheckedOutByCurrentUser &&
                                DataCacheService.hasUserConnectPlatform(46) && (
                                  <MenuItem
                                    key={intl.formatMessage(messages.Checkin)}
                                    onSelect={() => this.onCheckinDocument(template)}
                                  >
                                    <SaveIcon className="margin5R" />
                                    {intl.formatMessage(messages.Checkin)}
                                  </MenuItem>
                                )}
                              {isDocumentCheckedOut && (
                                <MenuItem
                                  key={intl.formatMessage(messages.DiscardCheckout)}
                                  onSelect={() => this.onDiscardCheckout(template)}
                                  // Disabled if user is not admin or checked out user
                                  disabled={
                                    actionsInProgress ||
                                    (!isDocumentCheckedOutByCurrentUser &&
                                      !authService.hasPermission({
                                        functionality: 'CompanyOwn',
                                        privilege: 'Update',
                                      }))
                                  }
                                >
                                  <UndoIcon className="margin5R" />
                                  {intl.formatMessage(messages.DiscardCheckout)}
                                </MenuItem>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </CanUpdate>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {showPreviewModal && (
          <PreviewDocumentTemplateModal
            templateId={activeTemplate && activeTemplate.id}
            onClose={() => this.setState({ showPreviewModal: false })}
          />
        )}
        {showAddModal && (
          <AddDocumentTemplateModal
            languages={languages}
            templateTypes={templateTypes}
            relationTypes={relationTypes}
            companies={companies}
            templateType={filters.templateType}
            language={filters.language || defaultLanguage}
            companyId={filters.companyId}
            onClose={template => this.appendTemplate(template)}
          />
        )}
        {showPlaceholdersModal && (
          <PlaceholdersModal
            templateTypes={templateTypes}
            templateType={filters.templateType}
            onClose={() => this.setState({ showPlaceholdersModal: false })}
          />
        )}
        {showTaskPlaceholdersModal && (
          <TaskPlaceholdersModal
            onClose={() => this.setState({ showTaskPlaceholdersModal: false })}
          />
        )}
        {deleteTemplate !== null && (
          <YesNoModal
            intl={intl}
            show
            title={intl.formatMessage(messages.DeleteTemplate)}
            message={intl.formatMessage(messages.DeleteTemplateConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                deleteTemplate: null,
              }))
            }
            onSave={() => this.deleteTemplate(deleteTemplate)}
          />
        )}
        {showCheckinDocumentModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DiscardCheckout)}
            message={intl.formatMessage(messages.CheckinDocumentConfirmationMsg, {
              name: checkedOutUserName,
            })}
            onNo={() =>
              this.setState({
                showCheckinDocumentModal: false,
                loadingDocumentDetails: false,
                actionsInProgress: false,
              })
            }
            onSave={() => this.discardCheckout(checkinTemplate)}
          />
        )}
      </div>
    );
  }
}

DocumentTemplateContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission('Settings', 'read')(injectIntl(DocumentTemplateContainer));
